import { create } from 'zustand';

import type { CategoricalPersonaField } from '../persona-lookalikes/lookalike-fields';

export type LookalikeViewMode = 'field' | 'similarity';
export type LookalikeDisplayMode = 'chart' | 'poll' | 'table';
/** The two continuous fields plus every categorical one — see
 *  `CategoricalPersonaField`'s own doc for where the latter come from. */
export type LookalikeAxisField =
  'age' | 'experienceYears' | CategoricalPersonaField;
export type LookalikeFilterField = CategoricalPersonaField;

/**
 * The Lookalikes modal's client state (CLAUDE.md > Frontend state) — which
 * persona's modal is open, and how the researcher is currently looking at the
 * population. The population and its spec are server state and live in
 * TanStack Query (`use-persona-lookalikes.ts`); this never duplicates them.
 */
interface PersonaLookalikesState {
  /** The persona the modal is open for, or `null` when it's closed. */
  personaId: string | null;
  view: LookalikeViewMode;
  displayMode: LookalikeDisplayMode;
  xField: LookalikeAxisField;
  yField: LookalikeAxisField;
  filterField: LookalikeFilterField | null;
  filterValue: string | null;
  open: (personaId: string) => void;
  close: () => void;
  setView: (view: LookalikeViewMode) => void;
  setDisplayMode: (mode: LookalikeDisplayMode) => void;
  setAxis: (axis: 'x' | 'y', field: LookalikeAxisField) => void;
  setFilter: (field: LookalikeFilterField | null, value: string | null) => void;
}

const initial = {
  displayMode: 'chart' as LookalikeDisplayMode,
  filterField: null,
  filterValue: null,
  personaId: null,
  view: 'field' as LookalikeViewMode,
  xField: 'experienceYears' as LookalikeAxisField,
  yField: 'age' as LookalikeAxisField,
};

export const usePersonaLookalikesStore = create<PersonaLookalikesState>()(
  (set) => ({
    ...initial,
    close: () => {
      set({ ...initial });
    },
    open: (personaId) => {
      set({ ...initial, personaId });
    },
    setAxis: (axis, field) => {
      set(axis === 'x' ? { xField: field } : { yField: field });
    },
    setDisplayMode: (displayMode) => {
      set({ displayMode });
    },
    setFilter: (filterField, filterValue) => {
      set({ filterField, filterValue });
    },
    setView: (view) => {
      set({ view });
    },
  }),
);

/** For tests: the store is a singleton and leaks between them. */
export function resetPersonaLookalikesStore(): void {
  usePersonaLookalikesStore.setState(initial);
}
